import axios from 'axios';
import { env } from '@core/config/env';
import { requestInterceptor, getAuthToken } from './interceptors/request-interceptor';
import { responseInterceptor, errorInterceptor } from './interceptors/response-interceptor';
import type { AxiosInstance } from 'axios';

/**
 * GraphQL client for resolving product codes via appGetProductTypeDetail query.
 *
 * Uses the same base URL and auth token as the REST API client,
 * but sends POST requests to /graphql endpoint.
 */
let graphqlClient: AxiosInstance | null = null;

/**
 * Get or create the GraphQL client instance.
 * Lazily initialized to ensure env is loaded and interceptors are configured.
 */
function getGraphqlClient(): AxiosInstance {
  if (graphqlClient) return graphqlClient;

  graphqlClient = axios.create({
    baseURL: env.apiBaseUrl,
    timeout: env.apiTimeoutMs,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  graphqlClient.interceptors.request.use(
    requestInterceptor,
    (error) => Promise.reject(error)
  );

  graphqlClient.interceptors.response.use(
    responseInterceptor,
    errorInterceptor
  );

  return graphqlClient;
}

export interface GraphQLResponse<T> {
  data: T;
  errors?: Array<{ message: string; locations?: Array<{ line: number; column: number }> }>;
}

/**
 * Execute a GraphQL query or mutation.
 *
 * @param query - The GraphQL query string
 * @param variables - Query variables
 * @returns Parsed response data (strips the outer `data` wrapper)
 */
export async function graphqlQuery<T>(
  query: string,
  variables: Record<string, unknown> = {}
): Promise<T> {
  const client = getGraphqlClient();

  // Ensure auth token is attached (interceptor handles this)
  const token = getAuthToken();
  if (token && !client.defaults.headers.common.Authorization) {
    client.defaults.headers.common.Authorization = `Bearer ${token}`;
  }

  const response = await client.post<GraphQLResponse<T>>('/graphql', {
    query,
    variables,
  });

  if (response.data.errors && response.data.errors.length > 0) {
    const messages = response.data.errors.map((e) => e.message).join(', ');
    throw new Error(`GraphQL error: ${messages}`);
  }

  return response.data.data;
}
